import {
  HubSpotContactDTO,
  HubSpotContactPropertiesDTO,
  HubSpotProperty,
} from "../../../api/src/services/hubspot-service";
import { useEffect, useState } from "react";
import {
  createFetcher,
  fetcherDefaults,
  useRequest,
  RequestParams,
} from "../use-request";
import { useMutation } from "@tanstack/react-query";
import vinoFetch from "../../utils/vinoFetch";
import useAuth from "../../hooks/use-auth";

export interface CommunicationPreferencesInterface {
  isLoading: boolean;
  communicationPreferences: HubSpotContactPropertiesDTO;
  updateCommunicationPreferences: (
    properties: Array<HubSpotProperty>
  ) => Promise<any>;
  refetchPreferences: () => Promise<any>;
  updateBigCommerceCustomer: (email: string) => Promise<any>;
  updateBigCommerceCustomerNames: (names: {
    first_name: string;
    last_name?: string;
  }) => Promise<any>;
  depersonaliseContact: () => Promise<any>;
  error?: string;
}

type HookParams = {
  getTokenID: () => Promise<string>;
};

const API_URL = `/api/get-hubspot-contact`;

export const useCommunicationPreferences = (): CommunicationPreferencesInterface => {
  const { getTokenID} = useAuth();
  const [preferences, setPreferences] = useState<HubSpotContactPropertiesDTO>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);

  const requestParams = {
    getTokenID,
  };
  const request = useRequest(createRequestParams(requestParams));
  const { fetcher } = createFetcher({
    ...fetcherDefaults,
    ...createRequestParams(requestParams),
  });
  const mutationRequest = useMutation({
    mutationKey: [requestParams.url],
    mutationFn: fetcher,
  });

  // Set loading state based on the request's loading state
  useEffect(() => {
    if (request) setIsLoading(request.isLoading);
  }, [request?.isLoading]);

  // Update preferences when new data is available
  useEffect(() => {
    if (request?.data) setPreferences(request.data.properties);
    if (request?.error) {
      const errorMessage = request.error?.response?.data;
      if (errorMessage === "HubSpot contact ID is undefined") {
        setError(undefined);
      } else {
        setError(
          request.error
            ? "Failed to fetch communication preferences"
            : undefined
        );
      }
    }
  }, [request?.data]);

  // Helper function to fetch access token and token ID and then perform the request
  const performRequestWithAuth = async (url: string, params: any = {}) => {
    try {
      const tokenID = await getTokenID();
      const headers = {
        authorization: `Bearer ${tokenID}`,
      };

      const { data } = await vinoFetch.get(url, { params, headers });
      return data;
    } catch (err) {
      setError("Failed to perform the request");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    communicationPreferences: preferences,
    refetchPreferences: async () => {
      setIsLoading(true);
      const data = await performRequestWithAuth("/api/get-hubspot-contact");
      if (data && data.properties) setPreferences(data.properties);
    },
    updateCommunicationPreferences: async (updatedPreferences) => {
      mutationRequest.mutate(
        { properties: { ...preferences, ...updatedPreferences } },
        false
      );
      await performRequestWithAuth(
        "/api/update-hubspot-contact",
        updatedPreferences
      );
      return mutationRequest.mutate();
    },
    updateBigCommerceCustomer: async (email) => {
      await performRequestWithAuth("/api/update-bigcommerce-contact", {
        email,
      });
      return mutationRequest.mutate();
    },
    updateBigCommerceCustomerNames: async (names: {
      first_name: string;
      last_name?: string;
    }) => {
      await performRequestWithAuth("/api/update-bigcommerce-contact", names);
      return mutationRequest.mutate();
    },
    depersonaliseContact: async () => {
      const tokenID = await getTokenID();

      await vinoFetch.post(
        "/api/depersonalise-contact",
        {},
        {
          headers: {
            authorization: `Bearer ${tokenID}`,
          },
        }
      );
      return mutationRequest.mutate();
    },
    error,
  };
};

const createRequestParams = ({
  getTokenID,
}: HookParams): RequestParams => {
  return {
    url: API_URL,
    getTokenID,
  };
};
